import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Users, MapPin, Calendar, Phone, Mail, Globe, Award, CheckCircle } from 'lucide-react';
import './GuidedToursPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GuidedToursPageNew() {
  const { t } = useTranslation();
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOperators();
  }, []);

  const fetchOperators = async () => {
    try {
      const response = await axios.get(`${API}/tour-operators`);
      setOperators(response.data);
    } catch (error) {
      console.error('Error fetching operators:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="guided-tours-page">
        <div className="loading-spinner"></div>
        <p>{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="guided-tours-page">
      <div className="page-header">
        <Users size={48} />
        <h1>Geführte Touren & Veranstalter</h1>
        <p className="subtitle">
          Professionelle Tourveranstalter für Wanderungen, Radtouren und Roadtrips in Schottland
        </p>
      </div>

      <div className="info-box">
        <h3>Wichtiger Hinweis</h3>
        <p>
          Die hier aufgeführten Anbieter wurden nach strengen Kriterien bewertet. 
          Nutzen Sie unsere Checkliste, um den perfekten Veranstalter zu finden.
        </p>
        <Link to="/knowledge/tour-operator-checklist" className="checklist-link">
          Zur Checkliste
        </Link>
      </div>

      <div className="operators-count">
        {operators.length} Anbieter gefunden
      </div>

      <div className="operators-grid">
        {operators.map((operator) => (
          <div key={operator.id} className="operator-card">
            <div className="operator-header">
              <h3>{operator.name}</h3>
              {operator.verified && (
                <span className="verified-badge">
                  <CheckCircle size={16} />
                  Verifiziert
                </span>
              )}
            </div>

            <p className="operator-description">{operator.description}</p>

            <div className="operator-types">
              {operator.tour_types?.map((type) => (
                <span key={type} className="tour-type-badge">
                  {type}
                </span>
              ))}
            </div>

            <div className="operator-details">
              {operator.location && (
                <div className="detail-item">
                  <MapPin size={16} />
                  <span>{operator.location}</span>
                </div>
              )}
              {operator.phone && (
                <div className="detail-item">
                  <Phone size={16} />
                  <a href={`tel:${operator.phone}`}>{operator.phone}</a>
                </div>
              )}
              {operator.email && (
                <div className="detail-item">
                  <Mail size={16} />
                  <a href={`mailto:${operator.email}`}>{operator.email}</a>
                </div>
              )}
            </div>

            {operator.scorecard && (
              <div className="operator-scorecard">
                <h4>
                  <Award size={16} />
                  Bewertung: {operator.scorecard.total_score}/100 Punkte
                </h4>
                <div className="scorecard-bars">
                  <div className="score-bar">
                    <span>Sicherheit</span>
                    <div className="bar-container">
                      <div 
                        className="bar-fill" 
                        style={{ width: `${operator.scorecard.safety}%` }}
                      />
                    </div>
                    <span>{operator.scorecard.safety}</span>
                  </div>
                  <div className="score-bar">
                    <span>Expertise</span>
                    <div className="bar-container">
                      <div 
                        className="bar-fill" 
                        style={{ width: `${operator.scorecard.expertise}%` }}
                      />
                    </div>
                    <span>{operator.scorecard.expertise}</span>
                  </div>
                  <div className="score-bar">
                    <span>Logistik</span>
                    <div className="bar-container">
                      <div 
                        className="bar-fill" 
                        style={{ width: `${operator.scorecard.logistics}%` }}
                      />
                    </div>
                    <span>{operator.scorecard.logistics}</span>
                  </div>
                </div>
              </div>
            )}

            {operator.website && (
              <a 
                href={operator.website.startsWith('http') ? operator.website : `https://${operator.website}`}
                target="_blank" 
                rel="noopener noreferrer"
                className="website-btn"
              >
                <Globe size={16} />
                Website besuchen
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
